import React, { useMemo } from 'react';
import { ISammanfattningRows } from '../../../EkonomiskAnalysSammanfattning';
import {
  BarPlot,
  LinePlot,
  MarkPlot,
  ChartsAxisHighlight,
  ChartsXAxis,
  ChartsYAxis,
  ChartsReferenceLine,
  HighlightOptions,
  FadeOptions,
  ChartsGrid,
  ChartsLegend,
  ChartDataProviderPro,
  ChartsSurface,
  BarItemIdentifier,
  LineSeriesType,
  BarSeriesType,
  ChartsXAxisProps,
  AxisConfig,
  ChartsYAxisProps,
} from '@mui/x-charts-pro';
import {
  capitalizeFirstLetter,
  getMonthText,
} from '../../../../../../../../../../Utils/dateUtils';
import { IEkonomiskAnalysFilledDates } from '../../../../AnalysSettingsHandler/AnalysSettingsHandler';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  INTAKTSUPPFOLJNING_IDKOD,
  KOSTNADSUPPFOLJNING_IDKOD,
  PrevColumnTypeEnum,
  RESULTATUPPFOLJNING_IDKOD,
  SpecifikationTypeEnum,
} from '../../../../../Const/EkonomiskAnalysConsts';
import { EkonomiskAnalysCustomAxisTooltip } from './EkonomiskAnalysCustomAxisTooltip';
import { Theme, useTheme } from '@mui/material/styles';
import { ZoomInteractionConfig } from '../../../../../Utils/detaljanalysUtils';
import { useFrontendUrl } from '../../../../../../../../../../Hooks/useFrontendUrl';
import { ISelection } from '../../../../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { ISumData } from '../../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { IUIUrval } from '../../../../../../../../../../Types/UrvalTypes';
import { borraIntaktEllerKostnad } from '../../../../../Utils/detaljAnalysDrillUtils';

interface IEkonomiskAnalysResultatChartProps {
  analysLevel: AnalysLevelEnum;
  analysType: AnalysTypeEnum;
  availableReports: ISelection[];
  currentKoddel: string | undefined;
  dates: IEkonomiskAnalysFilledDates;
  rows: ISammanfattningRows[];
  rubrikNiva: string;
  selectedAnalysCodeprivilegeID: string;
  selectedRowHeader: string | undefined;
  sums: ISumData[];
  valueFormatterCallback: (value: number | null) => string;
  urval: IUIUrval | null;
  zoomConfig: ZoomInteractionConfig;
}

interface IChartMonthData {
  month: number;
  intakt: number;
  kostnad: number;
  resultat: number;
}

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const EkonomiskAnalysResultatChart: React.FC<
  IEkonomiskAnalysResultatChartProps
> = ({
  analysLevel,
  analysType,
  availableReports,
  dates,
  rows,
  valueFormatterCallback,
  zoomConfig,
  rubrikNiva,
  selectedAnalysCodeprivilegeID,
  selectedRowHeader,
  sums,
  urval,
}) => {
  const theme = useTheme<Theme>();
  const frontendUrl = useFrontendUrl();
  const visibleMonths = useMemo<number[]>(() => {
    const clampedMonth = Math.min(Math.max(dates.from.month, 1), 12);
    return months.filter(month => month <= clampedMonth);
  }, [dates.from.month]);

  const chartData = useMemo<IChartMonthData[]>(() => {
    const monthData: IChartMonthData[] = [];
    for (let index = 0; index < visibleMonths.length; index++) {
      const month = visibleMonths[index];
      let monthChartPoint: IChartMonthData = {
        month: month,
        intakt: 0,
        kostnad: 0,
        resultat: 0,
      };
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        for (
          let prevPeriodIndex = 0;
          prevPeriodIndex < row.prevPeriods.length;
          prevPeriodIndex++
        ) {
          const prevPeriod = row.prevPeriods[prevPeriodIndex];
          if (
            prevPeriod.type === PrevColumnTypeEnum.CURRENT_YEAR &&
            prevPeriod.periodNumber === month
          ) {
            if (prevPeriod.specifikation === SpecifikationTypeEnum.KOSTNAD) {
              monthChartPoint.kostnad += prevPeriod.value;
            }
            if (prevPeriod.specifikation === SpecifikationTypeEnum.INTAKT) {
              monthChartPoint.intakt += prevPeriod.value;
            }
          }
        }
      }
      monthChartPoint.resultat =
        monthChartPoint.intakt - monthChartPoint.kostnad;

      monthData.push(monthChartPoint);
    }
    return monthData;
  }, [rows, visibleMonths]);

  const intaktData = useMemo(
    () => chartData.map(row => row.intakt),
    [chartData]
  );
  const kostnadData = useMemo(
    () => chartData.map(row => row.kostnad),
    [chartData]
  );
  const resultatData = useMemo(
    () => chartData.map(row => row.resultat),
    [chartData]
  );

  const onBarClick = (data: BarItemIdentifier) => {
    if (data.type === 'bar') {
      const dateWithClickedPeriod = {
        to: { ...dates.to, month: data.dataIndex + 1 },
        from: { ...dates.from, month: data.dataIndex + 1 },
      };
      let analysidKod = RESULTATUPPFOLJNING_IDKOD;
      if (data.seriesId === 'intakt') {
        analysidKod = INTAKTSUPPFOLJNING_IDKOD;
      } else if (data.seriesId === 'kostnad') {
        analysidKod = KOSTNADSUPPFOLJNING_IDKOD;
      }

      borraIntaktEllerKostnad(
        rubrikNiva,
        rubrikNiva, // Vid drilldown från resultatdiagrammet så vill vi inte byta ut den valda subnivån utan behålla den nuvarande rubriknivån
        selectedRowHeader ?? '',
        selectedAnalysCodeprivilegeID,
        urval,
        analysLevel,
        availableReports,
        dateWithClickedPeriod,
        analysidKod,
        frontendUrl,
        sums,
        true,
        analysType
      );
    }
  };

  const chartSeries: (BarSeriesType | LineSeriesType)[] = useMemo(() => {
    return [
      {
        type: 'bar',
        id: 'intakt',
        label: 'Intäkt',
        labelMarkType: 'square',
        data: intaktData,
        valueFormatter: valueFormatterCallback,
        color: theme.customProperties.dustyRainbow.glauca,
        highlightScope: {
          highlight: 'item' as HighlightOptions,
          fade: 'global' as FadeOptions,
        },
        xAxisId: 'monthsAxis',
        yAxisId: 'valueAxis',
      } as BarSeriesType,
      {
        type: 'bar',
        id: 'kostnad',
        label: 'Kostnad',
        data: kostnadData,
        labelMarkType: 'square',
        valueFormatter: valueFormatterCallback,
        color: theme.customProperties.dustyRainbow.salmon,
        highlightScope: {
          highlight: 'item' as HighlightOptions,
          fade: 'global' as FadeOptions,
        },
        xAxisId: 'monthsAxis',
        yAxisId: 'valueAxis',
      } as BarSeriesType,
      {
        type: 'line',
        id: 'resultat',
        label: 'Resultat',
        showMark: true,
        labelMarkType: 'line',
        data: resultatData,
        valueFormatter: valueFormatterCallback,
        color: theme.customProperties.customDetaljanalys.deepBlue,
        highlightScope: {
          highlight: 'item' as HighlightOptions,
          fade: 'global' as FadeOptions,
        },
        xAxisId: 'monthsAxis',
        yAxisId: 'valueAxis',
        curve: 'linear',
      } as LineSeriesType,
    ];
  }, [
    intaktData,
    kostnadData,
    resultatData,
    theme.customProperties.customDetaljanalys.deepBlue,
    theme.customProperties.dustyRainbow.glauca,
    theme.customProperties.dustyRainbow.salmon,
    valueFormatterCallback,
  ]);

  const chartXAxis: AxisConfig<'band', any, ChartsXAxisProps>[] =
    useMemo(() => {
      return [
        {
          disableLine: true,
          id: 'monthsAxis',
          scaleType: 'band',
          data: visibleMonths,
          tickInterval: visibleMonths,
          valueFormatter: (value: number, context: { location: string }) => {
            if (value === 0) {
              return '';
            }
            if (context.location === 'tooltip') {
              return capitalizeFirstLetter(getMonthText(value));
            }

            return capitalizeFirstLetter(getMonthText(value, true));
          },
          zoom: true,
        },
      ];
    }, [visibleMonths]);

  const chartYAxis: AxisConfig<'band', any, ChartsYAxisProps>[] =
    useMemo(() => {
      return [
        {
          id: 'valueAxis',
          valueFormatter: valueFormatterCallback,
          zoom: true,
          width: 100,
        },
      ];
    }, [valueFormatterCallback]);

  return (
    <ChartDataProviderPro
      height={600}
      series={chartSeries}
      xAxis={chartXAxis}
      yAxis={chartYAxis}
      zoomInteractionConfig={zoomConfig}
    >
      <EkonomiskAnalysCustomAxisTooltip
        currencyValues
        currentMonth={dates.from.month}
      />
      <ChartsLegend />
      <ChartsSurface>
        <ChartsAxisHighlight x="band" />
        <ChartsReferenceLine y={0} lineStyle={{ strokeWidth: 1 }} />
        <ChartsGrid horizontal />
        <ChartsXAxis axisId="monthsAxis" />
        <ChartsYAxis axisId="valueAxis" />
        <BarPlot onItemClick={(event, d) => onBarClick(d)} />
        <LinePlot slotProps={{ line: { strokeWidth: 5 } }} />
        <MarkPlot />
      </ChartsSurface>
    </ChartDataProviderPro>
  );
};

export default EkonomiskAnalysResultatChart;
